/**
 * The print routes: `/print/badge` and `/print/letter` render the print root with SAMPLE pages
 * so the PDF proof (`tools/proofs/pdf-pages.ts`) has a file to prove; `/print/checks/<run>`
 * renders a check run — one letter page per check, the check and its stub — for the finance
 * director to print on pre-printed stock. Real badges and day lists arrive with the volunteers
 * module.
 */
import type { ReactNode } from 'react';
import { PrintRoot, Sample } from '@cider/ui';
import { CheckPages } from './checks.js';
import { ReportPages } from './report.js';
import { PacketSummary } from './packet.js';
import { OrderPage } from './order.js';
import { AskLetters } from './asks.js';
import { ServiceLetterPage } from './service-letter.js';
import { ShortfallLetters } from './shortfall-letters.js';
import { IncidentLog } from './incidents.js';
import { BoothSheet, WaiverPad } from './booth.js';
import { ShelterRoll } from './shelter-roll.js';
import { KeyLog } from './key-log.js';
import { FieldCards } from './field-cards.js';
import { BoothCard } from './booth-card.js';
import { BagTag, CountSheet } from './count-sheets.js';
import { DepositCover } from './deposit-cover.js';
import { AreaPage } from './area-page.js';
import { RoleBadges } from './badges.js';
import { WindowSlip } from './window-slips.js';
import { GateSheet, RadioCard } from './field-forms.js';
import { SetupBinder } from './binder.js';
import { FinanceYearPoster, SlideDeck } from './documents.js';
import { AodPackage } from './aod-package.js';
import { ForumOnePager } from './forum.js';
import { AreaCloseout } from './closeout.js';
import { FulfillmentStatement } from './fulfillment.js';
import { YearEndReport } from './year-end.js';
import { BudgetBook } from './budget-book.js';
import { GamingCovers } from './gaming-covers.js';
import { Form5089 } from './form-5089.js';
import { Ten99Letters, W9Chase } from './payee-letters.js';
import { DonationReceipt, SponsorInvoice, SponsorReceipt } from './sponsor-papers.js';
import { ChairHandbook, NeedChairsPoster, SponsorDeck } from './people-facing.js';
import {
  BulletinCard,
  Certificate,
  ChairLetters,
  FamilyLetters,
  ThankYouLetters,
} from './letters.js';
import { SignInSheet } from './meeting.js';
import { BannerOrderPage, SponsorListPage, GiftReceipt } from './artwork.js';
import {
  BagLabels,
  CustodyPages,
  DepositSlip,
  IntakeForms,
  RouteSheet,
  SeedSheet,
} from './countroom.js';

export function PrintPage({ kind, id }: { kind: string | null; id: string | null }): ReactNode {
  if (kind === 'checks' && id) return <CheckPages runId={id} />;
  if (kind === 'report' && id) return <ReportPages kind={id} />;
  if (kind === 'seed-sheet' && id) return <SeedSheet year={id} />;
  if (kind === 'route-sheet' && id) return <RouteSheet day={id} />;
  if (kind === 'intake-form' && id) return <IntakeForms year={id} />;
  if (kind === 'deposit-slip' && id) return <DepositSlip id={id} />;
  if (kind === 'bag-labels' && id) return <BagLabels year={id} />;
  if (kind === 'custody' && id) return <CustodyPages ref={id} />;
  if (kind === 'packet' && id) return <PacketSummary year={id} />;
  // A purchase order on paper (A9): /print/po/<id>.
  if (kind === 'po' && id) return <OrderPage id={id} />;
  // The January ask letters (Sponsors Part 3): /print/ask-letters/<year>?asks=<ids>.
  if (kind === 'ask-letters' && id) return <AskLetters year={id} />;
  // A service-hours letter, sealed when signed (ADR 0029): /print/service-letter/<id>.
  if (kind === 'service-letter' && id) return <ServiceLetterPage id={id} />;
  // The school's shortfall letters that went on paper (ADR 0029): /print/shortfall-letters/<year>.
  if (kind === 'shortfall-letters' && id) return <ShortfallLetters year={id} />;
  // The meeting's sign-in sheet, with the RSVPs already on it (Doors in Part 2).
  if (kind === 'sign-in' && id) return <SignInSheet id={id} />;
  // The vendor's banner order and a gift in kind's receipt (Sponsors Part 4).
  // Watchtower's day log, printed at close (the ninth overnight).
  if (kind === 'incidents' && id) return <IncidentLog year={id} />;
  // The booth's paper (ON11 item 3): read by the station's own session.
  if (kind === 'booth-sheet') return <BoothSheet />;
  // Stream A · A14 (James, Oct 18): the shelter roll, printed at the admin station.
  if (kind === 'shelter-roll') return <ShelterRoll />;
  // Stream A · A08: the key log, from Festival Ops → Radios & keys.
  if (kind === 'key-log' && id) {
    const [year = '', day] = id.split(':');
    return <KeyLog year={year} day={day} />;
  }
  // Stream A · A04: the incident card and the lost-child card, blank, for the night the app is down.
  if (kind === 'field-cards' && id) return <FieldCards year={id} />;
  // Stream A · A12: the booth's table card.
  if (kind === 'booth-card') return <BoothCard />;
  // Stream D (James, Oct 18): the sponsorship deck, the "We need chairs" poster, the handbook.
  if (kind === 'sponsor-deck' && id) return <SponsorDeck year={id} />;
  if (kind === 'need-chairs' && id) return <NeedChairsPoster year={id} />;
  if (kind === 'chair-handbook' && id) return <ChairHandbook year={id} />;
  // Stream D · D05: the thank-you letters (every volunteer) and one person's certificate.
  if (kind === 'thank-you' && id) return <ThankYouLetters year={id} />;
  // Stream D · D03: the family's-year letter to every family, and the bulletin card.
  if (kind === 'family-letters' && id) return <FamilyLetters year={id} />;
  // Stream D · D04: this year's chairs asked again, and the chair prospects asked once.
  if (kind === 'chair-invitations' && id) return <ChairLetters year={id} who="returning" />;
  if (kind === 'prospect-letters' && id) return <ChairLetters year={id} who="prospects" />;
  if (kind === 'bulletin-card' && id) return <BulletinCard year={id} />;
  if (kind === 'certificate' && id?.includes(':')) {
    const [year = '', person = ''] = id.split(':');
    return <Certificate year={year} personId={person} />;
  }
  // Stream C (James, Oct 18): /print/doc/<document>:<fiscal year>[:<the one it is for>], and the finance-year poster.
  if (kind === 'doc' && id?.includes(':')) {
    // The third part names the one paper is for: an area, a receivable, a gift, a family.
    const [key = '', fy = '', one = ''] = id.split(':');
    return key === 'aod-package' ? (
      <AodPackage fy={Number(fy)} />
    ) : key === 'forum' ? (
      <ForumOnePager fy={Number(fy)} />
    ) : key === 'sponsor-invoice' && one ? (
      <SponsorInvoice fy={Number(fy)} id={one} />
    ) : key === 'sponsor-receipt' && one ? (
      <SponsorReceipt fy={Number(fy)} id={one} />
    ) : key === 'donation-receipt' && one ? (
      <DonationReceipt fy={Number(fy)} id={one} />
    ) : key === '1099-letters' ? (
      <Ten99Letters fy={Number(fy)} />
    ) : key === 'w9-chase' ? (
      <W9Chase fy={Number(fy)} />
    ) : key === 'form-5089' ? (
      <Form5089 fy={Number(fy)} />
    ) : key === 'gaming-covers' ? (
      <GamingCovers fy={Number(fy)} />
    ) : key === 'budget-book' ? (
      <BudgetBook fy={Number(fy)} />
    ) : key === 'year-end' ? (
      <YearEndReport fy={Number(fy)} />
    ) : key === 'area-closeout' && one ? (
      <AreaCloseout fy={Number(fy)} areaId={one} />
    ) : key === 'fulfillment' && one ? (
      <FulfillmentStatement year={Number(fy) - 1} familyId={one} />
    ) : key === 'finance-year-poster' ? (
      <FinanceYearPoster fy={Number(fy)} />
    ) : key === 'budget-slides' || key === 'closeout-slides' ? (
      <SlideDeck deck={key} fy={Number(fy)} />
    ) : (
      <p role="alert">No such document.</p>
    );
  }
  // Stream A · A09: the setup-week binder's cover and a tab a day.
  if (kind === 'setup-binder' && id) return <SetupBinder year={id} />;
  // Stream A · A05 · A07: the radio card and the gate clicker sheet, labels and lines.
  if (kind === 'radio-card' && id) return <RadioCard year={id} />;
  // `/print/gate-sheet/<year>[:<operating day>]`: a sheet per gate for the day.
  if (kind === 'gate-sheet' && id) {
    const [year = '', day] = id.split(':');
    return <GateSheet year={year} day={day} />;
  }
  // Stream A · A13: /print/will-call/<order code> · /print/bracelet-label/<order code>.
  if ((kind === 'will-call' || kind === 'bracelet-label') && id)
    return <WindowSlip kind={kind} code={id} />;
  // Stream A · A11: /print/role-badges/<year>, from Roles.
  if (kind === 'role-badges' && id) return <RoleBadges year={id} />;
  // Stream A · A10: /print/area-page/<year>:<area id>, through the chair's own doors.
  if (kind === 'area-page' && id?.includes(':')) {
    const [year = '', area = ''] = id.split(':');
    return <AreaPage year={year} areaId={area} />;
  }
  // Stream A · A03: /print/deposit-cover/<year>:<deposit id>.
  if (kind === 'deposit-cover' && id?.includes(':')) {
    const [year = '', dep = ''] = id.split(':');
    return <DepositCover year={year} id={dep} />;
  }
  // Stream A · A01–A02: /print/count-sheet/<year>:<bag> · /print/bag-tag/<year>:<bag>.
  if ((kind === 'count-sheet' || kind === 'bag-tag') && id?.includes(':')) {
    const [year = '', code = ''] = id.split(':');
    return kind === 'count-sheet' ? (
      <CountSheet year={year} code={code} />
    ) : (
      <BagTag year={year} code={code} />
    );
  }
  if (kind === 'waiver-pad')
    return <WaiverPad copies={Math.min(Math.max(Number(id ?? 10) || 10, 1), 50)} />;
  if (kind === 'banner-order' && id) return <BannerOrderPage year={id} />;
  // The program's sponsor page and the sponsor wall list (Track 3, 1e).
  if (kind === 'program-sponsors' && id) return <SponsorListPage year={id} which="program" />;
  if (kind === 'sponsor-wall' && id) return <SponsorListPage year={id} which="wall" />;
  if (kind === 'gift-receipt' && id) return <GiftReceipt id={id} />;
  if (kind === 'badge') {
    return (
      <PrintRoot
        kind="badge"
        title="Sample badges"
        pages={['Marge Kowalski', 'Rita'].map((name) => (
          <div key={name} className="badge">
            <div className="badge__eyebrow">SAINT LAWRENCE APPLE FEST · VOLUNTEER</div>
            <div className="badge__name">{name}</div>
            <div className="badge__area">Bakery</div>
            <Sample />
          </div>
        ))}
      />
    );
  }
  if (kind === 'letter') {
    return (
      <PrintRoot
        kind="letter"
        title="Sample day list"
        pages={[
          <div key="1" className="letter">
            <h1>Bakery — Saturday</h1>
            <p>The printed day list arrives with the volunteers module.</p>
            <Sample />
          </div>,
        ]}
      />
    );
  }
  return (
    <p>
      Print what? `/print/badge`, `/print/letter`, `/print/checks/&lt;run&gt;` or
      `/print/report/&lt;kind&gt;`.
    </p>
  );
}
